import React, { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import {
  getTickets,
  sendTicketResponse
} from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';

function Query() {
  const { permissions } = usePermissions();
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ticketData, setTicketData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [responseForm, setResponseForm] = useState({
    response: '',
    status: ''
  });
  const [formErrors, setFormErrors] = useState({
    response: '',
    status: ''
  });

  // Constants for validation
  const MAX_RESPONSE_LENGTH = 1000;
  const MIN_RESPONSE_LENGTH = 10;

  // Validation function
  const validateForm = () => {
    let isValid = true;
    const errors = {
      response: '',
      status: ''
    };

    // Response validation
    if (!responseForm.response.trim()) {
      errors.response = 'Response is required';
      isValid = false;
    } else if (responseForm.response.trim().length < MIN_RESPONSE_LENGTH) {
      errors.response = `Response must be at least ${MIN_RESPONSE_LENGTH} characters`;
      isValid = false;
    } else if (responseForm.response.trim().length > MAX_RESPONSE_LENGTH) {
      errors.response = `Response cannot exceed ${MAX_RESPONSE_LENGTH} characters`;
      isValid = false;
    }

    // Status validation
    if (!responseForm.status) {
      errors.status = 'Please select a status';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Fetch ticket data
  const fetchTicketData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTickets({
        page: pagination.currentPage,
        limit: pagination.recordsPerPage,
        search: search,
        status: statusFilter
      });

      if (response.success) {
        setTicketData(response.data.records);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to fetch ticket data');
      }
    } catch (error) {
      console.error('Error fetching ticket data:', error);
      toast.error('Error fetching ticket data');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.recordsPerPage, search, statusFilter]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchTicketData();
  }, [fetchTicketData]);

  const getStatusClass = (status) => {
    switch (status) {
      case 'Solved':
        return (
          <span className="px-2 py-1 rounded text-sm border border-green-500 bg-green-500/10 text-green-500 inline-flex items-center gap-1">
            <Icon icon="mdi:check-circle-outline" className="text-base" />
            Solved
          </span>
        );
      case 'Pending':
        return (
          <span className="px-2 py-1 rounded text-sm border border-yellow-500 bg-yellow-500/10 text-yellow-500 inline-flex items-center gap-1">
            <Icon icon="mdi:clock-outline" className="text-base" />
            Pending
          </span>
        );
      case 'In Progress':
        return (
          <span className="px-2 py-1 rounded text-sm border border-blue-500 bg-blue-500/10 text-blue-500 inline-flex items-center gap-1">
            <Icon icon="mdi:progress-clock" className="text-base" />
            In Progress
          </span>
        );
      case 'Cancelled':
        return (
          <span className="px-2 py-1 rounded text-sm border border-red-500 bg-red-500/10 text-red-500 inline-flex items-center gap-1">
            <Icon icon="mdi:close-circle-outline" className="text-base" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded text-sm border border-gray-500 bg-gray-500/10 text-gray-500 inline-flex items-center gap-1">
            <Icon icon="mdi:help-circle-outline" className="text-base" />
            Unknown
          </span>
        );
    }
  };

  const handleResponseClick = (ticket) => {
    if (!permissions.settings_query_edit) {
      toast.warning("You don't have permission to respond to queries");
      return;
    }
    setSelectedTicket(ticket);
    setResponseForm({
      response: ticket.response || '',
      status: ticket.status || ''
    });
    setShowResponseModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error when user starts typing
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Prevent typing if max length is reached for response
    if (name === 'response') {
      if (value.length <= MAX_RESPONSE_LENGTH) {
        setResponseForm(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setResponseForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    
    if (!permissions.settings_query_edit) {
      toast.warning("You don't have permission to respond to queries");
      return;
    }

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await sendTicketResponse(selectedTicket.id, {
        ...responseForm,
        response: responseForm.response.trim()
      });
      if (response.success) {
        toast.success('Ticket response sent successfully');
        fetchTicketData();
        setShowResponseModal(false);
        setSelectedTicket(null);
        resetForm();
      } else {
        toast.error(response.message || 'Failed to send ticket response');
      }
    } catch (error) {
      console.error('Error sending ticket response:', error);
      toast.error('Error sending ticket response');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setResponseForm({ response: '', status: '' });
    setFormErrors({ response: '', status: '' });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({
      ...prev,
      recordsPerPage: newLimit,
      currentPage: 1
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      {/* search and filter section */}
      {!permissions.settings_query_view ? (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
          <Icon icon="mdi:warning" className="mr-2 inline" />
          You don't have permission to view queries.
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
            <div className="relative w-full sm:w-64">
              <Icon
                icon="fluent:search-20-regular"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              />
              <input
                type="search"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="Search by Ticket ID, Subject..."
                value={search}
                onChange={handleSearchChange}
              />
            </div>
            <select
              className="px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm w-full sm:w-auto"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">All Status</option>
              <option value="Solved">Solved</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          {/* table */}
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
            <table className="min-w-[1200px] w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">SL No</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Ticket ID</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Email</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Subject</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Response</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Submitted Date</th>
                  <th className="px-4 py-2.5 text-center font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-12 text-center">
                      <Loader caption="Loading Queries..." minLoadTime={500} containerHeight="200px" />
                    </td>
                  </tr>
                ) : ticketData.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-12 text-center">
                      <NoData caption="No queries found" />
                    </td>
                  </tr>
                ) : (
                  ticketData.map((ticket, index) => (
                    <tr key={ticket.id} className="border-b last:border-b-0 hover:bg-gray-50/70">
                      <td className="px-4 py-3 text-gray-700">
                        {(pagination.currentPage - 1) * pagination.recordsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{ticket.ticket_id}</td>
                      <td className="px-4 py-3 text-gray-900">{ticket.sender_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-600">{ticket.sender_email || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-900">
                        <div className="line-clamp-2">{ticket.subject}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {ticket.response ? (
                          <div className="line-clamp-2">{ticket.response}</div>
                        ) : (
                          <span className="text-gray-400 italic">No response yet</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusClass(ticket.status)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(ticket.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <button
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!permissions.settings_query_edit ? "You don't have permission to respond" : "Add/Edit Response"}
                            onClick={() => handleResponseClick(ticket)}
                            disabled={loading || !permissions.settings_query_edit}
                          >
                            <Icon icon="fluent:chat-24-regular" width="18" height="18" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* record per page and pagination */}
          <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span className="hidden sm:inline">Rows per page:</span>
              <select
                className="px-3 py-1.5 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                value={pagination.recordsPerPage}
                onChange={handleLimitChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-gray-500">
                {pagination.totalRecords === 0
                  ? 'No records'
                  : `Showing ${(pagination.currentPage - 1) * pagination.recordsPerPage + 1}–${Math.min(
                      pagination.currentPage * pagination.recordsPerPage,
                      pagination.totalRecords
                    )} of ${pagination.totalRecords}`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <button
                type="button"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1 || loading || pagination.totalPages === 0}
                title="Previous page"
              >
                <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
              </button>
              <span className="px-2 text-gray-700">
                Page <span className="font-medium">{pagination.totalPages === 0 ? 0 : pagination.currentPage}</span> of{' '}
                <span className="font-medium">{pagination.totalPages || 0}</span>
              </span>
              <button
                type="button"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages || loading || pagination.totalPages === 0}
                title="Next page"
              >
                <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedTicket && permissions.settings_query_edit && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-3.5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h5 className="font-semibold text-base">Respond - {selectedTicket.ticket_id}</h5>
              <button
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => {
                  setShowResponseModal(false);
                  setSelectedTicket(null);
                  resetForm();
                }}
              >
                <Icon icon="fluent:dismiss-24-regular" width="20" height="20" />
              </button>
            </div>
            <div className="p-5">
              <form onSubmit={handleSubmitResponse}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 text-gray-900">{selectedTicket.sender_name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Phone:</span>
                      <span className="ml-2 text-gray-900">{selectedTicket.sender_phone || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 text-gray-900">{selectedTicket.sender_email || 'N/A'}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Subject:</span>
                    <span className="ml-2 text-gray-900">{selectedTicket.subject}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Summary:</span>
                    <p className="mt-1 text-gray-900">{selectedTicket.description || 'No description'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Response <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm ${formErrors.response ? 'border-red-500' : 'border-gray-200'}`}
                      name="response"
                      value={responseForm.response}
                      onChange={handleInputChange}
                      placeholder="Enter your response"
                      rows="3"
                      disabled={loading}
                    />
                    {formErrors.response && (
                      <div className="text-red-500 text-xs mt-1">{formErrors.response}</div>
                    )}
                    <small className="text-gray-500 text-xs mt-1 block">
                      {responseForm.response.length}/{MAX_RESPONSE_LENGTH}
                    </small>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm ${formErrors.status ? 'border-red-500' : 'border-gray-200'}`}
                      name="status"
                      value={responseForm.status}
                      onChange={handleInputChange}
                      disabled={loading}
                    >
                      <option value="">Select Status</option>
                      <option value="Solved">Solved</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                    {formErrors.status && (
                      <div className="text-red-500 text-xs mt-1">{formErrors.status}</div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
              <button
                type="button"
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-3.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                onClick={() => {
                  setShowResponseModal(false);
                  setSelectedTicket(null);
                  resetForm();
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-3.5 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                onClick={handleSubmitResponse}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Query
